
import { useState, useEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { EditGymDialog } from "@/components/gyms/edit-gym-dialog"
import { MoreHorizontalIcon, PencilIcon, TrashIcon } from "lucide-react"
import { type Gym, getGyms } from "@/api/gyms"
import { useToast } from "@/hooks/use-toast"
import { useConfirmationStore } from "@/lib/stores/confirmation-store"
import { apiClient } from "@/lib/api-client"
import { TableEmptyRow, TableLoadingRows } from "@/components/shared/table-states"

export function GymTable() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const confirmationStore = useConfirmationStore()

  const [gyms, setGyms] = useState<Gym[]>([])
  const [loading, setLoading] = useState(true)
  const [editingGym, setEditingGym] = useState<string | null>(null)

  const fetchGyms = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getGyms()
      setGyms(data)
    } catch (error) {
      console.error("Failed to fetch gyms:", error)
      toast({
        title: t("common.error"),
        description: t("gyms.errorFetchingGyms"),
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }, [t, toast])

  useEffect(() => {
    fetchGyms()
  }, [fetchGyms])

  // Handle gym deletion
  const handleDeleteGym = useCallback(
    (gym: Gym) => {
      confirmationStore.open({
        title: t("gyms.deleteGym"),
        message: t("gyms.deleteConfirmNamed", { name: gym.name }),
        variant: "destructive",
        confirmLabel: t("common.delete"),
        onConfirm: async () => {
          try {
            console.log(`Deleting gym ${gym._id}...`)
            await apiClient.delete(`/gyms/${gym._id}`)

            toast({
              title: t("common.success"),
              description: t("gyms.gymDeleted"),
            })

            fetchGyms()
          } catch (error) {
            console.error(`Error deleting gym ${gym._id}:`, error)

            toast({
              title: t("common.error"),
              description: t("gyms.deleteError"),
              variant: "destructive",
            })
          }
        },
      })
    },
    [fetchGyms, t, toast, confirmationStore],
  )

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("gyms.name")}</TableHead>
            <TableHead>{t("gyms.address")}</TableHead>
            <TableHead>{t("gyms.city")}</TableHead>
            <TableHead>{t("gyms.phone")}</TableHead>
            <TableHead>{t("gyms.status")}</TableHead>
            <TableHead className="text-right">{t("common.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableLoadingRows colSpan={6} />
          ) : gyms.length === 0 ? (
            <TableEmptyRow colSpan={6} message={t("gyms.noGyms")} />
          ) : (
            gyms.map((gym) => (
            <TableRow key={gym._id}>
              <TableCell className="font-medium">{gym.name}</TableCell>
              <TableCell>{gym.address}</TableCell>
              <TableCell>{gym.city}</TableCell>
              <TableCell>{gym.phone}</TableCell>
              <TableCell>
                <Badge variant={gym.status === "active" ? "default" : "secondary"}>
                  {gym.status === "active" ? t("gyms.active") : t("gyms.inactive")}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontalIcon className="h-4 w-4" />
                      <span className="sr-only">{t("common.actions")}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setEditingGym(gym._id)}>
                      <PencilIcon className="h-4 w-4 mr-2" />
                      {t("common.edit")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDeleteGym(gym)}>
                      <TrashIcon className="h-4 w-4 mr-2" />
                      {t("common.delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {editingGym && (
        <EditGymDialog
          gymId={editingGym}
          open={!!editingGym}
          onOpenChange={() => setEditingGym(null)}
          onGymUpdated={fetchGyms}
        />
      )}
    </>
  )
}
